import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Send, Info } from "lucide-react";

interface MessageInputProps {
  conversationId: number;
  onSend: (message: string) => void;
  disabled?: boolean;
}

export function MessageInput({
  conversationId,
  onSend,
  disabled = false,
}: MessageInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autofocus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || disabled) return;

    onSend(message);
    setMessage("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  return (
    <div className="w-full flex relative z-[2]">
      <div className="w-full rounded-lg border border-slate-300 bg-slate-50 p-2 shadow-lg">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex flex-col relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Posez une question..."
              disabled={disabled}
              className="w-full min-h-[64px] max-h-[200px] resize-none rounded-lg border border-gray-300 px-3 py-2 pb-10 text-sm focus:border-default-500 focus:outline-none focus:ring-1 focus:ring-default-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              rows={2}
            />

            <div className="absolute right-4 bottom-2 flex items-center gap-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Info size={14} className="flex-shrink-0" />
                <span>
                  Utiliser la recherche pour identifier des profils{" "}
                  <strong className="font-medium">
                    villes, emplacements, enseignes et candidats
                  </strong>
                </span>
              </div>
              <button
                type="submit"
                disabled={!message.trim() || disabled}
                className="flex items-center gap-2 rounded-lg bg-default-600 px-4 py-2 text-sm font-medium text-white hover:bg-default-700 focus:outline-none focus:ring-2 focus:ring-default-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                Envoyer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
